import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import BrandHeader from '../components/BrandHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Stripe OAuth return landing page (redirect_uri).
 * Stripe redirects here with ?code=...&state=... after the user authorises.
 * We exchange the code, attach the account to the club, then bounce to the
 * admin dashboard (the backend issued the session token via go-live email).
 */
export default function StripeConnectedPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState('connecting'); // connecting | success | error
  const [error, setError] = useState('');
  // Guard against StrictMode double-mount / accidental re-render double-POSTs.
  // The state token is single-use on the backend — a second POST would fail with
  // "Invalid or expired state" and clobber the successful first POST's result.
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;
    const code = searchParams.get('code');
    const oauthState = searchParams.get('state');
    const err = searchParams.get('error');

    if (err) {
      setState('error');
      setError(searchParams.get('error_description') || err);
      return;
    }
    if (!code || !oauthState) {
      setState('error');
      setError('Stripe did not return a valid authorisation code.');
      return;
    }

    exchangedRef.current = true;
    axios.post(`${API}/onboard/stripe-oauth-callback`, { code, state: oauthState })
      .then(r => {
        setState('success');
        // Hop to welcome page which will poll stripe-status, grant session token,
        // and auto-redirect into the admin dashboard.
        setTimeout(() => {
          navigate(`/worldcup2026/${r.data.slug}/welcome?session=oauth`);
        }, 1500);
      })
      .catch(e => {
        setState('error');
        setError(e.response?.data?.detail || 'Could not link your Stripe account.');
      });
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <BrandHeader />

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md text-center">
          {state === 'connecting' && (
            <div data-testid="oauth-connecting">
              <div className="w-12 h-12 border-4 border-[#635BFF]/30 border-t-[#635BFF] rounded-full animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-black text-[#1D3557] mb-2">Linking your Stripe account…</h1>
              <p className="text-gray-600">One moment.</p>
            </div>
          )}
          {state === 'success' && (
            <div data-testid="oauth-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <svg width="32" height="32" fill="none" stroke="#16a34a" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
              <h1 className="text-2xl font-black text-[#1D3557] mb-2">Stripe connected!</h1>
              <p className="text-gray-600">Taking you to your dashboard…</p>
            </div>
          )}
          {state === 'error' && (
            <div data-testid="oauth-error">
              <h1 className="text-2xl font-bold text-[#1D3557] mb-3">Could not link Stripe</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <a href="mailto:support@targetfunder.com" className="inline-block px-6 py-2.5 bg-[#1D3557] text-white font-bold rounded-lg">Contact support</a>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
